<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { geometryHasGuides, guidePathsForFrame, normalizeGeometry, type ProductGeometry } from '@pfe/design-renderer'
import { drawFrame2d, frame2dPadFraction, type Frame2d } from '../../preview3d/flat2d'
import { maxCropForAspect as sharedMaxCropForAspect, normRot as sharedNormRot } from '../../utils/orientation'
import { isCoarsePointer } from '../../config/breakpoints'

type Coords = { left: number; top: number; width: number; height: number }
type MoveMode = 'move' | 'nw' | 'ne' | 'se' | 'sw' | 'n' | 'e' | 's' | 'w' | 'pinch' | null
export type GuidesMode = 'full' | 'cut-only' | 'off'

const props = withDefaults(
  defineProps<{
    src: string
    disabled?: boolean
    grayscale?: boolean
    rotation?: number
    coordinates?: Coords | null
    /**
     * aspectRatio: Seitenverhältnis des Crops (bei Beschnitt bereits inkl. Beschnitt).
     * geometry:    Produktgeometrie (Form, Beschnitt, Sicherheitsbereich) -> Overlay
     *              "So wird produziert". guides steuert die Sichtbarkeit.
     */
    stencilProps?: { aspectRatio?: number; geometry?: Partial<ProductGeometry> | null; guides?: GuidesMode; whiteBorder?: boolean; frame2d?: Frame2d | null }
    maxDevicePixelRatio?: number
    overlayOnly?: boolean
    imageOnly?: boolean
    imageWidth?: number
    imageHeight?: number
  }>(),
  { disabled: false, rotation: 0, maxDevicePixelRatio: 2, overlayOnly: false, imageOnly: false, imageWidth: 0, imageHeight: 0 },
)

const emit = defineEmits<{
  (e: 'ready', payload: any): void
  (e: 'change', payload: any): void
  (e: 'complete', payload: any): void
  (e: 'tap', event: PointerEvent): void
}>()

const wrapEl = ref<HTMLElement | null>(null)
const canvasEl = ref<HTMLCanvasElement | null>(null)

const img = new Image()
const imgW = ref(0)
const imgH = ref(0)
const rotate = ref(0)
const crop = ref<Coords>({ left: 0, top: 0, width: 10, height: 10 })
let ro: ResizeObserver | null = null
let raf = 0
let changeRaf = 0
let moveMode: MoveMode = null
let pointerId = -1
let startPoint = { x: 0, y: 0 }
let startCrop: Coords = { left: 0, top: 0, width: 10, height: 10 }
// Zwei-Finger-Geste (Pinch): Ausschnitt um das Pinch-Zentrum skalieren.
const activePointers = new Map<number, { x: number; y: number }>()
let pinchStart: { dist: number; center: { x: number; y: number }; crop: Coords } | null = null
let lastPerfMs = 0
let movedDuringPointer = false
let suppressExternalCoordinatesUntil = 0
let refreshResumeTimer: ReturnType<typeof setTimeout> | null = null

function n(v: any, fallback = 0) {
  const x = Number(v)
  return Number.isFinite(x) ? x : fallback
}

function clamp(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v))
}

function normRot(value: any) {
  return sharedNormRot(value)
}

/** Geometrie aus stencilProps (nur wenn sie etwas zu zeigen hat). */
function activeGeometry(): ProductGeometry | null {
  const raw = (props.stencilProps as any)?.geometry
  if (!raw) return null
  const g = normalizeGeometry(raw)
  return geometryHasGuides(g) ? g : null
}

function guidesMode(): GuidesMode {
  const m = (props.stencilProps as any)?.guides
  return m === 'off' || m === 'cut-only' ? m : 'full'
}

// Schraffur für den Beschnittbereich ("wird abgeschnitten"), einmal erzeugt.
let hatchPattern: CanvasPattern | null = null
function getHatchPattern(ctx: CanvasRenderingContext2D): CanvasPattern | null {
  if (hatchPattern) return hatchPattern
  const tile = document.createElement('canvas')
  tile.width = 8
  tile.height = 8
  const t = tile.getContext('2d')
  if (!t) return null
  t.strokeStyle = 'rgba(255,255,255,0.55)'
  t.lineWidth = 1.2
  t.beginPath()
  t.moveTo(-2, 10)
  t.lineTo(10, -2)
  t.moveTo(-2, 2)
  t.lineTo(2, -2)
  t.moveTo(6, 10)
  t.lineTo(10, 6)
  t.stroke()
  hatchPattern = ctx.createPattern(tile, 'repeat')
  return hatchPattern
}

const coarsePointer = isCoarsePointer()

function imageSpaceSize() {
  const sourceW = Math.max(1, imgW.value)
  const sourceH = Math.max(1, imgH.value)
  const rot = normRot(rotate.value)
  if (rot === 90 || rot === 270) {
    return { width: sourceH, height: sourceW, rotated: true as const, rotation: rot }
  }
  return { width: sourceW, height: sourceH, rotated: false as const, rotation: rot }
}

function effectiveDpr() {
  return Math.max(1, Math.min(Number(props.maxDevicePixelRatio || 2) || 2, window.devicePixelRatio || 1))
}

function getAspectRatio() {
  const ar = n((props.stencilProps as any)?.aspectRatio, 0)
  return ar > 0 ? ar : 0
}

// External `coordinates` are authored in the declared source space (props.imageWidth/
// imageHeight, rotation-aware), but the actually-loaded preview can be any resolution
// (thumbnail, editor preview, or a server/remote preview after reload). Convert
// to/from the loaded image space so the crop stays correct regardless of which preview
// is shown. When the declared space is unknown (props missing) we fall back to the
// loaded space (legacy behaviour, no scaling).
function declaredCoordSpace(): { width: number; height: number } | null {
  const dw = Math.max(0, Number(props.imageWidth || 0) || 0)
  const dh = Math.max(0, Number(props.imageHeight || 0) || 0)
  if (!(dw > 0) || !(dh > 0)) return null
  const rot = normRot(rotate.value)
  return rot === 90 || rot === 270 ? { width: dh, height: dw } : { width: dw, height: dh }
}

function scaleCoords(c: Coords, from: { width: number; height: number }, to: { width: number; height: number }): Coords {
  const sx = to.width / Math.max(1, from.width)
  const sy = to.height / Math.max(1, from.height)
  return { left: c.left * sx, top: c.top * sy, width: c.width * sx, height: c.height * sy }
}

function externalToLoaded(c: Coords): Coords {
  const decl = declaredCoordSpace()
  return decl ? scaleCoords(c, decl, imageSpaceSize()) : c
}

function loadedToExternal(c: Coords): Coords {
  const decl = declaredCoordSpace()
  return decl ? scaleCoords(c, imageSpaceSize(), decl) : c
}

function maxCropForAspect(width: number, height: number, ar: number): Coords {
  return sharedMaxCropForAspect(width, height, ar)
}

function fitRect(cw: number, ch: number) {
  const space = imageSpaceSize()
  const iw = Math.max(1, space.width)
  const ih = Math.max(1, space.height)
  // Rahmen (Schattenfuge, Leiste …) außen um den Ausschnitt: dafür Rand freihalten
  const pad = frame2dPadFraction((props.stencilProps as any)?.frame2d ?? null) * Math.min(cw, ch)
  const s = Math.min((cw - 2 * pad) / iw, (ch - 2 * pad) / ih)
  const w = iw * s
  const h = ih * s
  const x = (cw - w) / 2
  const y = (ch - h) / 2
  return { x, y, w, h }
}

function toImagePoint(clientX: number, clientY: number) {
  const canvas = canvasEl.value
  if (!canvas) return { x: 0, y: 0 }
  const r = canvas.getBoundingClientRect()
  const dpr = effectiveDpr()
  const cw = canvas.width / dpr
  const ch = canvas.height / dpr
  const box = fitRect(cw, ch)
  const px = clientX - r.left
  const py = clientY - r.top
  const space = imageSpaceSize()
  const x = ((px - box.x) / Math.max(1, box.w)) * Math.max(1, space.width)
  const y = ((py - box.y) / Math.max(1, box.h)) * Math.max(1, space.height)
  return {
    x: clamp(x, 0, Math.max(1, space.width)),
    y: clamp(y, 0, Math.max(1, space.height)),
  }
}

function cropCanvasRect(cw: number, ch: number) {
  const box = fitRect(cw, ch)
  const space = imageSpaceSize()
  const sx = box.w / Math.max(1, space.width)
  const sy = box.h / Math.max(1, space.height)
  return {
    x: box.x + crop.value.left * sx,
    y: box.y + crop.value.top * sy,
    w: crop.value.width * sx,
    h: crop.value.height * sy,
  }
}

function hitTest(clientX: number, clientY: number): MoveMode {
  if (props.imageOnly) return null
  const canvas = canvasEl.value
  if (!canvas) return null
  const r = canvas.getBoundingClientRect()
  const dpr = effectiveDpr()
  const cw = canvas.width / dpr
  const ch = canvas.height / dpr
  const cr = cropCanvasRect(cw, ch)
  const x = clientX - r.left
  const y = clientY - r.top
  // Griffe: auf Touch-Geräten deutlich größere Trefferfläche.
  const hs = coarsePointer ? 22 : 14

  const handles: Record<'nw' | 'ne' | 'se' | 'sw', { x: number; y: number }> = {
    nw: { x: cr.x, y: cr.y },
    ne: { x: cr.x + cr.w, y: cr.y },
    se: { x: cr.x + cr.w, y: cr.y + cr.h },
    sw: { x: cr.x, y: cr.y + cr.h },
  }

  for (const [k, p] of Object.entries(handles) as Array<[Exclude<MoveMode, 'move' | null>, { x: number; y: number }]>) {
    if (Math.abs(x - p.x) <= hs && Math.abs(y - p.y) <= hs) return k
  }

  if (x >= cr.x && x <= cr.x + cr.w && y >= cr.y && y <= cr.y + cr.h) return 'move'
  return null
}

function normalizeCrop(next: Coords) {
  const space = imageSpaceSize()
  const maxW = Math.max(2, space.width)
  const maxH = Math.max(2, space.height)
  let left = clamp(Math.round(next.left), 0, maxW - 2)
  let top = clamp(Math.round(next.top), 0, maxH - 2)
  let width = Math.max(2, Math.round(next.width))
  let height = Math.max(2, Math.round(next.height))

  if (left + width > maxW) width = Math.max(2, maxW - left)
  if (top + height > maxH) height = Math.max(2, maxH - top)

  left = clamp(left, 0, Math.max(0, maxW - width))
  top = clamp(top, 0, Math.max(0, maxH - height))

  crop.value = { left, top, width, height }
}

function applyMove(mode: MoveMode, x: number, y: number) {
  if (!mode) return
  const ar = getAspectRatio()
  const minSize = 8
  const space = imageSpaceSize()
  const maxW = Math.max(2, space.width)
  const maxH = Math.max(2, space.height)
  const dx = x - startPoint.x
  const dy = y - startPoint.y
  const c = { ...startCrop }

  if (mode === 'move') {
    const width = Math.max(2, Math.round(c.width))
    const height = Math.max(2, Math.round(c.height))
    const left = clamp(Math.round(c.left + dx), 0, Math.max(0, maxW - width))
    const top = clamp(Math.round(c.top + dy), 0, Math.max(0, maxH - height))
    crop.value = { left, top, width, height }
    return
  }

  let left = c.left
  let top = c.top
  let right = c.left + c.width
  let bottom = c.top + c.height

  if (mode.includes('w')) left = clamp(c.left + dx, 0, right - minSize)
  if (mode.includes('e')) right = clamp(c.left + c.width + dx, left + minSize, maxW)
  if (mode.includes('n')) top = clamp(c.top + dy, 0, bottom - minSize)
  if (mode.includes('s')) bottom = clamp(c.top + c.height + dy, top + minSize, maxH)

  let width = right - left
  let height = bottom - top

  if (ar > 0) {
    if (mode === 'e' || mode === 'w') {
      height = Math.round(width / ar)
      if (height > maxH) {
        height = maxH
        width = Math.round(height * ar)
      }
      top = clamp(c.top + (c.height - height) / 2, 0, maxH - height)
    } else {
      width = Math.round(height * ar)
      if (width > maxW) {
        width = maxW
        height = Math.round(width / ar)
      }
      left = clamp(c.left + (c.width - width) / 2, 0, maxW - width)
    }
  }

  normalizeCrop({ left, top, width, height })
}

function draw() {
  cancelAnimationFrame(raf)
  raf = requestAnimationFrame(() => {
    const canvas = canvasEl.value
    if (!canvas || !imgW.value || !imgH.value) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = effectiveDpr()
    const cw = canvas.width / dpr
    const ch = canvas.height / dpr
    const box = fitRect(cw, ch)
    const rot = normRot(rotate.value)

    ctx.clearRect(0, 0, cw, ch)
    if (!props.overlayOnly) {
      ctx.save()
      ctx.filter = props.grayscale ? 'grayscale(1)' : 'none'
      ctx.translate(box.x + box.w / 2, box.y + box.h / 2)
      if (rot) ctx.rotate((rot * Math.PI) / 180)
      const drawW = rot === 90 || rot === 270 ? box.h : box.w
      const drawH = rot === 90 || rot === 270 ? box.w : box.h
      ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH)
      ctx.restore()
    }

    if (props.imageOnly) return

    const cr = cropCanvasRect(cw, ch)
    ctx.save()
    ctx.fillStyle = 'rgba(10, 14, 24, 0.58)'
    ctx.beginPath()
    ctx.rect(box.x, box.y, box.w, box.h)
    ctx.rect(cr.x, cr.y, cr.w, cr.h)
    ctx.fill('evenodd')
    ctx.restore()

    drawWhiteBorderPreview(ctx, cr)
    const frame2d = (props.stencilProps as any)?.frame2d as Frame2d | null
    if (frame2d) drawFrame2d(ctx, cr, frame2d, () => draw())

    ctx.strokeStyle = 'rgba(255,255,255,0.96)'
    ctx.lineWidth = 1.5
    ctx.strokeRect(cr.x, cr.y, cr.w, cr.h)

    const points = [
      [cr.x, cr.y],
      [cr.x + cr.w, cr.y],
      [cr.x + cr.w, cr.y + cr.h],
      [cr.x, cr.y + cr.h],
    ]
    for (const [x, y] of points) {
      ctx.beginPath()
      ctx.fillStyle = 'rgba(255,255,255,0.98)'
      ctx.strokeStyle = 'rgba(15,23,42,0.45)'
      ctx.lineWidth = 1
      ctx.arc(x, y, coarsePointer ? 6 : 4.5, 0, Math.PI * 2)
      ctx.fill()
      ctx.stroke()
    }

    drawProductionGuides(ctx, cr)
  })
}

/**
 * Option „weißer Rand“: nur eine Vorschau, nicht maßstabsgetreu – die echte Randbreite
 * legt die Produktion fest.
 */
function drawWhiteBorderPreview(ctx: CanvasRenderingContext2D, cr: { x: number; y: number; w: number; h: number }) {
  if (!(props.stencilProps as any)?.whiteBorder) return
  if (!(cr.w > 8) || !(cr.h > 8)) return
  const t = Math.max(2, Math.round(Math.min(cr.w, cr.h) * 0.015))
  ctx.save()
  ctx.fillStyle = '#fff'
  ctx.beginPath()
  ctx.rect(cr.x, cr.y, cr.w, cr.h)
  ctx.rect(cr.x + t, cr.y + t, cr.w - 2 * t, cr.h - 2 * t)
  ctx.fill('evenodd')
  // feine Innenkante, damit der Rand auch auf hellen Motiven erkennbar ist
  ctx.strokeStyle = 'rgba(15,23,42,0.18)'
  ctx.lineWidth = 1
  ctx.strokeRect(cr.x + t + 0.5, cr.y + t + 0.5, cr.w - 2 * t - 1, cr.h - 2 * t - 1)
  ctx.restore()
}

/**
 * "So wird produziert": Der Crop-Rahmen ist das volle Format (Endformat + Beschnitt).
 * Innerhalb davon: schraffiert = Beschnitt (wird abgeschnitten), gestrichelt = Schnittkante
 * (bei Formprodukten die Kontur), dünn = Sicherheitsbereich.
 */
function drawProductionGuides(ctx: CanvasRenderingContext2D, cr: { x: number; y: number; w: number; h: number }) {
  const g = activeGeometry()
  if (!g) return
  const mode = guidesMode()
  if (mode === 'off') return
  if (!(cr.w > 2) || !(cr.h > 2)) return

  // Orientierung: Crop kann gegenüber der Geometrie gedreht sein (Hoch-/Querformat).
  const fullW = g.trimWidthMm + 2 * g.bleedMm
  const fullH = g.trimHeightMm + 2 * g.bleedMm
  const swapped = fullW !== fullH && (cr.w >= cr.h) !== (fullW >= fullH)
  const guides = guidePathsForFrame(g, cr, swapped)

  ctx.save()
  // Beschnitt: Bereich zwischen Rahmen und Schnittkante schraffieren
  if (g.bleedMm > 0 || g.shape !== 'rect') {
    const region = new Path2D(guides.bleedPath + ' ' + guides.cutPath)
    const pattern = getHatchPattern(ctx)
    ctx.fillStyle = 'rgba(10, 14, 24, 0.28)'
    ctx.fill(region, 'evenodd')
    if (pattern) {
      ctx.fillStyle = pattern
      ctx.fill(region, 'evenodd')
    }
  }

  // Schnittkante: dunkle Unterlinie + weiße gestrichelte Linie (auf hellen und dunklen Motiven sichtbar)
  const cut = new Path2D(guides.cutPath)
  ctx.setLineDash([])
  ctx.strokeStyle = 'rgba(15, 23, 42, 0.55)'
  ctx.lineWidth = 3
  ctx.stroke(cut)
  ctx.setLineDash([6, 4])
  ctx.strokeStyle = 'rgba(255,255,255,0.98)'
  ctx.lineWidth = 1.5
  ctx.stroke(cut)

  // Sicherheitsbereich
  if (mode === 'full' && guides.safePath) {
    const safe = new Path2D(guides.safePath)
    ctx.setLineDash([2, 3])
    ctx.strokeStyle = 'rgba(255,255,255,0.75)'
    ctx.lineWidth = 1
    ctx.stroke(safe)
  }
  ctx.restore()
}

function scheduleChangeEmit(perfMs?: number, kind: 'change' | 'complete' = 'change') {
  if (Number.isFinite(perfMs)) lastPerfMs = Number(perfMs)
  if (changeRaf) return
  changeRaf = requestAnimationFrame(() => {
    changeRaf = 0
    emitState(kind, lastPerfMs)
  })
}

function emitState(evt: 'ready' | 'change' | 'complete', perfMs?: number) {
  // Report coordinates back in the declared source space (matches what callers store
  // as sourceWidth/sourceHeight), independent of the loaded preview resolution.
  const decl = declaredCoordSpace() || imageSpaceSize()
  const out = loadedToExternal({
    left: crop.value.left,
    top: crop.value.top,
    width: crop.value.width,
    height: crop.value.height,
  })
  const payload = {
    imageSize: { width: Math.max(1, decl.width), height: Math.max(1, decl.height) },
    coordinates: {
      left: Math.round(out.left),
      top: Math.round(out.top),
      width: Math.round(out.width),
      height: Math.round(out.height),
    },
    imageTransforms: { rotate: rotate.value },
    perfMs,
  }
  emit(evt, payload)
}

function resizeCanvas() {
  const wrap = wrapEl.value
  const canvas = canvasEl.value
  if (!wrap || !canvas) return
  const dpr = effectiveDpr()
  const w = Math.max(10, Math.round(wrap.clientWidth))
  const h = Math.max(10, Math.round(wrap.clientHeight))
  canvas.width = Math.round(w * dpr)
  canvas.height = Math.round(h * dpr)
  const ctx = canvas.getContext('2d')
  if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  draw()
}

function refreshAfterResume() {
  if (!wrapEl.value || !canvasEl.value) return
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
  if (refreshResumeTimer) {
    clearTimeout(refreshResumeTimer)
    refreshResumeTimer = null
  }

  const run = () => {
    resizeCanvas()
    applyExternalCoordinates()
    draw()
  }

  run()
  requestAnimationFrame(() => {
    run()
    requestAnimationFrame(run)
  })

  refreshResumeTimer = setTimeout(() => {
    refreshResumeTimer = null
    run()
  }, 140)
}

function onWindowFocus() {
  refreshAfterResume()
}

function onPageShow() {
  refreshAfterResume()
}

function onVisibilityChange() {
  if (typeof document === 'undefined') return
  if (document.visibilityState !== 'visible') return
  refreshAfterResume()
}

function pinchMetrics() {
  const pts = [...activePointers.values()]
  if (pts.length < 2) return null
  const a = toImagePoint(pts[0]!.x, pts[0]!.y)
  const b = toImagePoint(pts[1]!.x, pts[1]!.y)
  return {
    dist: Math.max(1, Math.hypot(b.x - a.x, b.y - a.y)),
    center: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
  }
}

/** Ausschnitt um `factor` skalieren (< 1 = kleiner = Zoom in), zentriert auf `center`, AR erhalten. */
function scaleCropAround(base: Coords, factor: number, center: { x: number; y: number }) {
  const ar = getAspectRatio() || base.width / Math.max(1, base.height)
  const space = imageSpaceSize()
  const maxW = Math.max(2, space.width)
  const maxH = Math.max(2, space.height)
  let width = clamp(base.width * factor, 8, maxW)
  let height = ar > 0 ? width / ar : base.height * factor
  if (height > maxH) {
    height = maxH
    width = ar > 0 ? height * ar : width
  }
  if (height < 8) {
    height = 8
    width = ar > 0 ? height * ar : width
  }
  // Relativer Anker: Pinch-Zentrum bleibt an derselben Stelle im Ausschnitt.
  const relX = base.width > 0 ? (center.x - base.left) / base.width : 0.5
  const relY = base.height > 0 ? (center.y - base.top) / base.height : 0.5
  normalizeCrop({
    left: center.x - relX * width,
    top: center.y - relY * height,
    width,
    height,
  })
}

function beginPinch() {
  const m = pinchMetrics()
  if (!m) return
  moveMode = 'pinch'
  movedDuringPointer = true
  pinchStart = { dist: m.dist, center: m.center, crop: { ...crop.value } }
}

function onPointerDown(e: PointerEvent) {
  if (props.disabled || !imgW.value || !imgH.value) return
  if (props.imageOnly) return
  activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY })
  ;(e.currentTarget as HTMLElement)?.setPointerCapture?.(e.pointerId)

  if (activePointers.size >= 2) {
    suppressExternalCoordinatesUntil = 0
    beginPinch()
    return
  }

  const mode = hitTest(e.clientX, e.clientY)
  if (!mode) return
  suppressExternalCoordinatesUntil = 0
  moveMode = mode
  pointerId = e.pointerId
  movedDuringPointer = false
  startPoint = toImagePoint(e.clientX, e.clientY)
  startCrop = { ...crop.value }
}

function onPointerMove(e: PointerEvent) {
  if (activePointers.has(e.pointerId)) activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY })

  if (moveMode === 'pinch') {
    const m = pinchMetrics()
    if (!m || !pinchStart) return
    const t0 = performance.now()
    const factor = pinchStart.dist / m.dist
    const shifted = {
      x: m.center.x,
      y: m.center.y,
    }
    // Basis-Crop um den Pinch-Versatz verschieben, dann skalieren
    const base: Coords = {
      ...pinchStart.crop,
      left: pinchStart.crop.left + (m.center.x - pinchStart.center.x),
      top: pinchStart.crop.top + (m.center.y - pinchStart.center.y),
    }
    scaleCropAround(base, factor, shifted)
    draw()
    scheduleChangeEmit(performance.now() - t0)
    return
  }

  if (pointerId !== e.pointerId || !moveMode) return
  const t0 = performance.now()
  const p = toImagePoint(e.clientX, e.clientY)
  if (!movedDuringPointer) {
    const dx = p.x - startPoint.x
    const dy = p.y - startPoint.y
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) movedDuringPointer = true
  }
  applyMove(moveMode, p.x, p.y)
  draw()
  scheduleChangeEmit(performance.now() - t0)
}

function onPointerUp(e: PointerEvent) {
  const wasTracked = activePointers.delete(e.pointerId)
  ;(e.currentTarget as HTMLElement)?.releasePointerCapture?.(e.pointerId)

  if (moveMode === 'pinch') {
    // Geste endet, sobald ein Finger geht; der verbleibende Finger startet keine neue Bewegung.
    moveMode = null
    pinchStart = null
    pointerId = -1
    suppressExternalCoordinatesUntil = performance.now() + 140
    if (activePointers.size === 0) emitState('complete', lastPerfMs)
    return
  }

  if (pointerId !== e.pointerId) {
    if (!wasTracked) return
    return
  }
  const shouldTap = !movedDuringPointer
  suppressExternalCoordinatesUntil = performance.now() + 140
  moveMode = null
  pointerId = -1
  emitState('complete', lastPerfMs)
  if (shouldTap) emit('tap', e)
}

/** Trackpad-Pinch / Strg+Rad: Ausschnitt um den Mauszeiger zoomen. */
function onWheel(e: WheelEvent) {
  if (props.disabled || props.imageOnly || !imgW.value || !imgH.value) return
  if (!(e.ctrlKey || e.metaKey)) return
  e.preventDefault()
  const center = toImagePoint(e.clientX, e.clientY)
  const factor = Math.exp(clamp(e.deltaY, -60, 60) * 0.004)
  suppressExternalCoordinatesUntil = performance.now() + 240
  scaleCropAround({ ...crop.value }, factor, center)
  draw()
  scheduleChangeEmit(0)
  emitState('complete', lastPerfMs)
}

function loadImage(src: string) {
  if (!src) return
  rotate.value = normRot(props.rotation)
  const knownW = Math.max(0, Number(props.imageWidth || 0) || 0)
  const knownH = Math.max(0, Number(props.imageHeight || 0) || 0)
  if (props.overlayOnly && knownW > 0 && knownH > 0) {
    imgW.value = knownW
    imgH.value = knownH
    if (!props.imageOnly && props.coordinates) normalizeCrop(externalToLoaded(props.coordinates))
    else crop.value = maxCropForAspect(imgW.value, imgH.value, getAspectRatio())
    resizeCanvas()
    nextTick(() => emitState('ready'))
    return
  }
  img.onload = () => {
    imgW.value = Math.max(1, img.naturalWidth || 1)
    imgH.value = Math.max(1, img.naturalHeight || 1)
    if (!props.imageOnly) {
      if (props.coordinates) normalizeCrop(externalToLoaded(props.coordinates))
      else crop.value = maxCropForAspect(imgW.value, imgH.value, getAspectRatio())
    }
    resizeCanvas()
    if (!props.imageOnly) nextTick(() => emitState('ready'))
  }
  img.src = src
}

function sameCoords(a: Coords, b: Coords) {
  return (
    Math.round(n(a.left, 0)) === Math.round(n(b.left, 0)) &&
    Math.round(n(a.top, 0)) === Math.round(n(b.top, 0)) &&
    Math.round(n(a.width, 0)) === Math.round(n(b.width, 0)) &&
    Math.round(n(a.height, 0)) === Math.round(n(b.height, 0))
  )
}

function applyExternalCoordinates() {
  if (props.imageOnly) return
  if (!imgW.value || !imgH.value) return
  if (moveMode || pointerId >= 0) return
  if (performance.now() < suppressExternalCoordinatesUntil) return
  const coords = props.coordinates
  if (!coords) return
  const loadedCoords = externalToLoaded(coords)
  if (sameCoords(crop.value, loadedCoords)) return
  normalizeCrop(loadedCoords)
  draw()
}

function setCoordinates(coords: Coords) {
  normalizeCrop(externalToLoaded(coords))
  draw()
}

function setImageTransforms(t: { rotate?: number }) {
  rotate.value = n(t?.rotate, 0)
  draw()
}

function setTransforms(t: { rotate?: number }) {
  setImageTransforms(t)
}

function setRotation(r: number) {
  rotate.value = n(r, 0)
  draw()
}

defineExpose({ setCoordinates, setImageTransforms, setTransforms, setRotation })

watch(() => props.src, (src) => loadImage(src), { immediate: true })
watch(() => props.grayscale, () => {
  draw()
})
watch(() => [(props.stencilProps as any)?.guides, (props.stencilProps as any)?.whiteBorder, JSON.stringify((props.stencilProps as any)?.geometry ?? null)], () => {
  draw()
})
watch(() => JSON.stringify((props.stencilProps as any)?.frame2d ?? null), () => {
  draw()
})
watch(() => props.rotation, (next) => {
  const normalized = normRot(next)
  if (normalized === normRot(rotate.value)) return
  rotate.value = normalized
  draw()
})
watch(() => props.imageWidth, () => {
  if (!props.overlayOnly || !props.src) return
  loadImage(props.src)
})
watch(() => props.imageHeight, () => {
  if (!props.overlayOnly || !props.src) return
  loadImage(props.src)
})
watch(() => [props.imageWidth, props.imageHeight], () => {
  if (!props.overlayOnly || !props.src) return
  loadImage(props.src)
})
watch(() => rotate.value, () => {
  if (!imgW.value || !imgH.value) return
  if (!props.imageOnly && props.coordinates) normalizeCrop(externalToLoaded(props.coordinates))
  else if (!props.imageOnly) crop.value = maxCropForAspect(imageSpaceSize().width, imageSpaceSize().height, getAspectRatio())
  draw()
})
watch(
  () => [props.coordinates?.left, props.coordinates?.top, props.coordinates?.width, props.coordinates?.height, imgW.value, imgH.value, props.imageWidth, props.imageHeight],
  () => {
    applyExternalCoordinates()
  },
)
watch(() => props.stencilProps?.aspectRatio, (nextAr, prevAr) => {
  if (!imgW.value || !imgH.value) return
  if (nextAr !== prevAr && !props.imageOnly) {
    if (props.coordinates) normalizeCrop(externalToLoaded(props.coordinates))
    else crop.value = maxCropForAspect(imgW.value, imgH.value, getAspectRatio())
  }
  draw()
})

onMounted(() => {
  ro = new ResizeObserver(() => resizeCanvas())
  if (wrapEl.value) ro.observe(wrapEl.value)
  resizeCanvas()
  window.addEventListener('focus', onWindowFocus)
  window.addEventListener('pageshow', onPageShow)
  document.addEventListener('visibilitychange', onVisibilityChange)
})

onBeforeUnmount(() => {
  if (ro) ro.disconnect()
  window.removeEventListener('focus', onWindowFocus)
  window.removeEventListener('pageshow', onPageShow)
  document.removeEventListener('visibilitychange', onVisibilityChange)
  if (refreshResumeTimer) clearTimeout(refreshResumeTimer)
  cancelAnimationFrame(raf)
  cancelAnimationFrame(changeRaf)
})
</script>

<template>
  <div
    ref="wrapEl"
    class="cc"
    @pointerdown.stop="onPointerDown"
    @pointermove.stop="onPointerMove"
    @pointerup.stop="onPointerUp"
    @pointercancel.stop="onPointerUp"
    @wheel="onWheel"
    @click.stop
    @dblclick.stop
  >
    <canvas ref="canvasEl" class="cc-canvas" />
  </div>
</template>

<style scoped>
.cc {
  position: relative;
  width: 100%;
  height: 100%;
  touch-action: none;
}

.cc-canvas {
  width: 100%;
  height: 100%;
  display: block;
}
</style>
